import { Alert, Button, Modal } from 'react-bootstrap';
import moment from 'moment';

import { PraSection } from '../../../../../../__gqltypes__';
import { formatHms, PRA_SECTION_LABELS } from '../utils';

type Props = {
  show: boolean;
  section: PraSection;
  // No config saved yet: nobody can be mid-cycle — a plain confirm, never a hard warning.
  isNewConfig: boolean;
  maintenance: boolean;
  safeToEditAt: Date | null;
  onCancel: () => void;
  onConfirm: () => void;
};

// Confirmation modal on every save (MarathonEventData pattern) — HARD-WARNS when saving
// while maintenance is OFF or before the safe-to-edit time.
export function PraSaveConfirmModal({
  show,
  section,
  isNewConfig,
  maintenance,
  safeToEditAt,
  onCancel,
  onConfirm,
}: Props): JSX.Element {
  const now = Date.now();
  const beforeSafeTime = maintenance && safeToEditAt !== null && now < safeToEditAt.getTime();
  const hardWarn = !isNewConfig && (!maintenance || beforeSafeTime);
  const label = PRA_SECTION_LABELS[section];

  return (
    <Modal show={show} onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{hardWarn ? `Risky save — ${label}` : `Save ${label} configuration`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isNewConfig && (
          <Alert variant="info">
            This creates the section&apos;s first configuration — no players can be mid-cycle yet.
          </Alert>
        )}
        {!isNewConfig && !maintenance && (
          <Alert variant="danger">
            <strong>Maintenance is OFF for {label}.</strong> Players can start a new cycle at any moment and
            players waiting on cooldown will re-enter on the edited config. Recommended procedure: turn
            maintenance ON, wait for the safe-to-edit time, save, then turn maintenance OFF.
          </Alert>
        )}
        {!isNewConfig && maintenance && beforeSafeTime && safeToEditAt && (
          <Alert variant="danger">
            <strong>Before the safe-to-edit time.</strong> Cycles anchored before the maintenance flip can still
            be running until <strong>{moment(safeToEditAt).format('YYYY-MM-DD HH:mm:ss')}</strong> (safe in{' '}
            {formatHms(safeToEditAt.getTime() - now)}). Mid-cycle players stay on their config snapshot, but
            saving now is outside the agreed safe procedure.
          </Alert>
        )}
        {!isNewConfig && !hardWarn && (
          <Alert variant="info">
            Maintenance is ON and the safe-to-edit time has passed. Please confirm the save.
          </Alert>
        )}
        <p className="mb-0">
          Saving fully replaces the <strong>{label}</strong> section configuration on the server.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant={hardWarn ? 'danger' : 'primary'} onClick={onConfirm}>
          {hardWarn ? 'Save anyway' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
